import { PlusIcon } from "@heroicons/react/24/outline";
import React, {
  type DragEvent,
  type FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getMetricsFocusOptions } from "./analysisBuilderConstants";
import {
  type ReportSection,
  ReportSectionCard,
  type ReportSectionConfig,
} from "./ReportSectionCard";

const createEmptySection = (): ReportSection => ({
  id: Date.now().toString(),
  title: "",
  config: {
    metricsFocus: "",
    analysisStructure: "",
    visualization: "",
  },
  description: "",
});

interface AnalysisSectionsProps {
  sections: ReportSection[];
  onSectionsChange: (sections: ReportSection[]) => void;
  isDisabled?: boolean;
  movementTypeSlug?: string;
}

export const AnalysisSections: FC<AnalysisSectionsProps> = ({
  sections,
  onSectionsChange,
  isDisabled = false,
  movementTypeSlug,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(),
  );

  const metricsFocusOptions = useMemo(
    () => getMetricsFocusOptions(movementTypeSlug),
    [movementTypeSlug],
  );

  // Auto-expand when only one section remains
  useEffect(() => {
    if (sections.length === 1 && collapsedSections.has(sections[0].id)) {
      setCollapsedSections(new Set());
    }
  }, [sections.length, collapsedSections, sections]);

  // Reset each section's Metrics & Focus selection whenever the movement
  // type changes, since the available options are scoped to it. Latest
  // sections/onSectionsChange are read via refs so the effect only needs to
  // re-run on an actual movementTypeSlug change.
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const onSectionsChangeRef = useRef(onSectionsChange);
  onSectionsChangeRef.current = onSectionsChange;

  const previousMovementTypeSlugRef = useRef(movementTypeSlug);
  useEffect(() => {
    if (previousMovementTypeSlugRef.current === movementTypeSlug) return;
    previousMovementTypeSlugRef.current = movementTypeSlug;
    onSectionsChangeRef.current(
      sectionsRef.current.map((section) => ({
        ...section,
        config: { ...section.config, metricsFocus: "" },
      })),
    );
  }, [movementTypeSlug]);

  const addSection = () => {
    onSectionsChange([...sections, createEmptySection()]);
  };

  const updateSection = (id: string, updates: Partial<ReportSection>) => {
    onSectionsChange(
      sections.map((section) =>
        section.id === id ? { ...section, ...updates } : section,
      ),
    );
  };

  const removeSection = (id: string) => {
    if (sections.length > 1) {
      onSectionsChange(sections.filter((section) => section.id !== id));
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex === null) return;

    const newSections = [...sections];
    const [draggedSection] = newSections.splice(draggedIndex, 1);
    newSections.splice(dropIndex, 0, draggedSection);

    onSectionsChange(newSections);
    setDraggedIndex(null);
  };

  const toggleCollapse = (id: string) => {
    const newCollapsedSections = new Set(collapsedSections);
    if (newCollapsedSections.has(id)) {
      newCollapsedSections.delete(id);
    } else {
      newCollapsedSections.add(id);
    }
    setCollapsedSections(newCollapsedSections);
  };

  return (
    <div
      className={`flex flex-col gap-6 ${isDisabled ? "opacity-50 pointer-events-none" : ""}`}
      data-testid="analysis-sections"
    >
      {/* Section Header */}
      <h2
        className="text-white font-medium"
        data-testid="analysis-sections-title"
      >
        Analysis Sections
        <span className="ml-2 text-[#7e7e81] text-xs font-medium">
          (Optional)
        </span>
      </h2>

      {/* Section Cards */}
      {sections.map((section, index) => (
        <ReportSectionCard
          key={section.id}
          section={section}
          sectionNumber={index + 1}
          onUpdate={(updates) => updateSection(section.id, updates)}
          onRemove={() => removeSection(section.id)}
          canRemove={sections.length > 1}
          totalSections={sections.length}
          onDragStart={() => handleDragStart(index)}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDrop={() => handleDrop(index)}
          isCollapsed={collapsedSections.has(section.id)}
          onToggleCollapse={() => toggleCollapse(section.id)}
          isDisabled={isDisabled}
          metricsFocusOptions={metricsFocusOptions}
          isMetricsFocusDisabled={!movementTypeSlug}
        />
      ))}

      {/* Add Section Button */}
      <div className="flex flex-col gap-3 items-center">
        <button
          type="button"
          onClick={addSection}
          disabled={isDisabled}
          data-testid="analysis-sections-add-button"
          className="bg-[#1d1d1f] border border-white/10 rounded-lg px-6 py-3 flex items-center gap-3 hover:bg-[#252527] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="w-5 h-5 text-[#fb6a07]" />
          <span className="text-white">Add another report section</span>
        </button>
      </div>
    </div>
  );
};

export type { ReportSection, ReportSectionConfig };
